// Vendors
import Vue from 'vue';
import ShardsVue from 'shards-vue';
import Axios from 'axios';
import DOMPurify from 'dompurify';
import VueAxios from 'vue-axios';
import VueSweetalert2 from 'vue-sweetalert2';
import 'sweetalert2/dist/sweetalert2.all.min.js'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate';
import vSelect from 'vue-select';
import { mask } from 'vue-the-mask';
import VueWysiwyg from 'vue-wysiwyg';

// Styles
import 'bootstrap/dist/css/bootstrap.css';
import '@/scss/shards-dashboards.scss';
import '@/assets/scss/date-range.scss';
import '@/assets/scss/vue-select.override.scss';
import '@/assets/scss/form-popups.scss';

// Core
import App from './App.vue';
import AppConfig from './.env';
import router from './router';
import store from './store';
import SanitizeUtils from './utils/SanitizeUtils';

// Layouts
import Default from '@/layouts/Default.vue';
import GuestLayout from '@/layouts/Guest.vue';

// Helpers
import addVeeValidateRules from '@/utils/VeeValidateRules.js';
import FormUtils from './utils/FormUtils.js';

// Common parts
import Toolbar from '@/components/common/Toolbar.vue';

// Custom components
import FileInput from '@/components/common/FileInput.vue';

// DOMPurify configuration
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  // set all elements owning target to target=_blank
  if ('target' in node) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }

  // set non-HTML/MathML links to xlink:show=new
  if (!node.hasAttribute('target') && (node.hasAttribute('xlink:href') || node.hasAttribute('href'))) {
    node.setAttribute('xlink:show', 'new');
  }
});

// Global components
Vue.component('file-input', FileInput);
Vue.component('guest-layout', GuestLayout);
Vue.component('validation-provider', ValidationProvider);
Vue.component('validation-observer', ValidationObserver);
Vue.component('v-select', vSelect);
Vue.component('toolbar', Toolbar);

// Global directives
Vue.directive('mask', mask);

// Configure vendors
addVeeValidateRules(extend);

// Axios
window.axios = Axios;
Vue.prototype.$http = Axios;
Vue.prototype.$http.defaults.baseURL = AppConfig.apiServerUrl;
Vue.prototype.$googleMapsApiKey = AppConfig.googleMapsApiKey;

if (SanitizeUtils.getToken()) {
  Vue.prototype.$http.defaults.headers.Authorization = 'Bearer ' + SanitizeUtils.getToken();
  Vue.prototype.$http.defaults.headers['Cache-Control'] = 'no-store,max-age=0';
}

Vue.prototype.$http.interceptors.response.use(undefined, (error) => {
  if (error.response.status === 401 && router.currentRoute.path !== '/login') {
    window.localStorage.setItem('token', '');
    window.localStorage.removeItem('last-token-refresh');
    window.location.reload();
  }

  return Promise.reject(error);
});

// Register v-pure-html directive
Vue.directive('pure-html', {
  inserted (el, binding) {
    if (binding.oldValue === binding.value) {
      return;
    }
    el.innerHTML = DOMPurify.sanitize(binding.value);
  },
  update (el, binding) {
    if (binding.oldValue === binding.value) {
      return;
    }
    el.innerHTML = DOMPurify.sanitize(binding.value);
  }
});

// Shards
ShardsVue.install(Vue);
Vue.component('default-layout', Default);
Vue.config.productionTip = false;
Vue.prototype.$bus = new Vue();

// Other
Vue.use(VueAxios, Axios);
Vue.use(VueSweetalert2);
Vue.use(VueWysiwyg, {
  hideModules: {
    'headings': true,
    'code': true,
    'quote': true,
    'table': true,
    'image': true,
    'link': true
  },
  maxHeight: '200px'
});

new Vue({
  data: {
    userLoggedIn: false,
    loadedAirports: false,
    loadedContactPersons: false,
    airports: [],
    airportsLoadInProgress: false,
    contactPersons: [],
    contactPersonsLoadInProgress: false,
    lastClick: +new Date(),
    showInactivityAlert: false
  },
  computed: {
    enabledAirports () {
      return this.airports.filter(airport => airport.disabled === 0);
    }
  },
  watch: {
    userLoggedIn (newValue, oldValue) {
      if (oldValue !== newValue && newValue === true) {
        this.loadAirports();
        this.loadContactPersons();
        this.loadProfile();
      } else if (oldValue !== newValue && newValue === false) {
        this.airports = [];
        this.contactPersons = [];
        this.loadedAirports = false;
        this.loadedContactPersons = false;
        this.resetProfile();
      }
    }
  },
  mounted () {
    if (SanitizeUtils.getToken()) {
      this.userLoggedIn = true;
    }

    this.$bus.$on('calendar-reload-airports', this.loadAirports);
    this.$bus.$on('airports-list-reload', this.loadAirports);
    this.$bus.$on('contact-persons-list-reload', this.loadContactPersons);
  },
  methods: {
    loadAirports () {
      this.airportsLoadInProgress = true;

      FormUtils.loadAdditionalData(this, {
        endpoint: '/airports/items?simple=1',
        params: {
          pagination: 0
        },
        loadedKey: 'loadedAirports',
        outputKey: 'airports',
        errorKey: 'loadError',
        successAction: () => {
          this.airportsLoadInProgress = false;
        },
        failAction: () => {
          this.airportsLoadInProgress = false;
        }
      });
    },
    loadProfile () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/profile/item',
        loadedKey: null,
        outputKey: null,
        errorKey: null,
        successAction: (response) => {
          if (response.must_change_password) {
            setTimeout(() => {
              if (this.$route.path !== '/dashboard/change-password/force' && this.$route.path !== '/loading') {
                window.location.href = '/dashboard/change-password/force';
              }
            }, 2500);
          }

          this.$store.commit('setUser', response);
        }
      });
    },
    loadContactPersons () {
      this.contactPersonsLoadInProgress = true;

      FormUtils.loadAdditionalData(this, {
        endpoint: '/israel-contact-persons/items',
        params: {
          pagination: 0
        },
        loadedKey: 'loadedContactPersons',
        outputKey: 'contactPersons',
        errorKey: 'loadError',
        successAction: () => {
          this.contactPersonsLoadInProgress = false;
        },
        failAction: () => {
          this.contactPersonsLoadInProgress = false;
        }
      });
    },
    resetProfile() {
      this.$store.commit('setUser', null);
    }
  },
  beforeDestroy () {
    this.$bus.$off('calendar-reload-airports', this.loadAirports);
    this.$bus.$off('airports-list-reload', this.loadAirports);
    this.$bus.$off('contact-persons-list-reload', this.loadContactPersons);
  },
  store,
  router,
  render: h => h(App)
}).$mount('#app');
